/**
 * 运营后台 API 客户端（§3.3 响应包的 admin 侧对偶）。
 * ★ 四条纪律：
 *   1) 只有 `{ok:true}` 算成功；`error.code` 原样上抛，由页面决定文案（错误码翻译集中在中文页里，后台不做多语言）
 *   2) 401/403 与业务失败分开：401 ⇒ 会话过期（15m JWT），必须通知 store 立刻回登录页，
 *      而不是在表格里显示"没有数据" —— 运营看到"空列表"会以为订单真的没来（这是最坏的假信号）
 *   3) 写操作一律带 `reason/note`（C7/C5/C8 的审计要求由服务端强制，前端少传就 422）
 *   4) ★ 前端不装"权限判定"当防线：`can()` 只用于隐藏入口，真正的 RBAC 在 requireAdmin(c,'admin')
 */
import type {
  AdminBackendVersionRow,
  AdminCategoryRow,
  AdminConfigRow,
  AdminDeploymentRow,
  AdminLicenseRow,
  AdminOrderRow,
  AdminProfile,
  AdminSession,
  AdminTagRow,
  AdminTemplateRow,
  AdminUpgradeBatchRow,
  AdminUserRow,
  AdminVersionRow,
  AdvanceBatchResult,
  AuditLogRow,
  CreateBatchResult,
  DashboardResp,
  DeployPrecheck,
  GithubIntegrationView,
  I18nListResult,
  I18nPendingItem,
  IssueLicenseInput,
  LinkHealthRow,
  OrderStatusResult,
  PagedRows,
  PromoteResult,
  RotateTokenResult,
  SupportRequestRow,
  TotpEnabledResult,
  TotpEnrollResult,
  TotpStatus,
  UpgradeBatchItemRow,
} from './types';

export const API_BASE: string = (import.meta.env?.VITE_API_BASE ?? 'http://127.0.0.1:8787').replace(/\/$/, '');

/**
 * ★ `fetch` 被拒绝时浏览器只给一句 "Failed to fetch"：DNS 污染/超时、Worker 未部署、
 *   CORS 未放行本页 origin 三种原因在 JS 侧**完全同形**。只靠异常文本猜会把运维引向错的方向
 *   （2026-10 staging 真实踩过：登录页报“请先跑 pnpm api:dev”，实际是 *.workers.dev 被 DNS 污染）。
 *   这里用一次 `mode:'no-cors'` 的 /healthz 探针把「主机可达」与「请求被拒」分开：
 *   no-cors 请求不参与 CORS 判定 ⇒ 能拿到响应就说明域名/网络/Worker 都活着，失败只可能是白名单。
 *   ★ 只在浏览器 + 真 fetch 下探测：单测注入的 fetchImpl 不应被额外调用；
 *     10s 内复用结果，避免 5 个并发接口同时失败时打 5 次探针。
 */
const PROBE_TTL_MS = 10_000;
let probeCache: { at: number; ok: boolean } | null = null;

async function probeReachable(baseUrl: string): Promise<boolean | null> {
  if (typeof window === 'undefined') return null;
  if (probeCache && Date.now() - probeCache.at < PROBE_TTL_MS) return probeCache.ok;
  let ok = false;
  try {
    await fetch(`${baseUrl}/healthz`, { mode: 'no-cors', cache: 'no-store' });
    ok = true;
  } catch {
    ok = false;
  }
  probeCache = { at: Date.now(), ok };
  return ok;
}

export class ApiFailure extends Error {
  readonly code: string;
  readonly status: number;
  /** 服务端 Err 的 error.params（缺哪个权限、允许的下一状态等），页面直接展示 */
  readonly params: Record<string, unknown>;

  constructor(code: string, status: number, params: Record<string, unknown> = {}) {
    super(`${code} (${status})`);
    this.name = 'ApiFailure';
    this.code = code;
    this.status = status;
    this.params = params;
  }

  get retryable(): boolean {
    return this.status === 429 || this.status >= 500 || this.status === 0;
  }

  /** 会话失效（未登录 / token 过期 / 密钥族不认）⇒ 必须回登录页 */
  get unauthorized(): boolean {
    return this.status === 401 || this.status === 403;
  }
}

interface Envelope<T> {
  ok: boolean;
  data?: T;
  meta?: { next_cursor?: string; has_more: boolean; limit: number };
  error?: { code?: string; msgKey?: string; params?: Record<string, unknown> };
}

export interface Paged<T> {
  items: T[];
  meta?: Envelope<unknown>['meta'];
}

export type FetchLike = (input: string, init?: RequestInit) => Promise<Response>;

export interface ClientOptions {
  baseUrl?: string;
  fetchImpl?: FetchLike;
  token?: () => string | null;
  onUnauthorized?: () => void;
}

export function createAdminClient(options: ClientOptions = {}) {
  const baseUrl = (options.baseUrl ?? API_BASE).replace(/\/$/, '');
  const doFetch: FetchLike = options.fetchImpl ?? ((input, init) => fetch(input, init));
  const tokenOf = options.token ?? (() => null);
  /**
   * ★ 401 闸门：一个后台页面会并发打 4~5 个接口，token 过期时它们**同时**失败。
   * 每个都回调 ⇒ 路由被连推 5 次（回登录页后 query 里的 next 还会被后面的推掉）。
   * 闸门在任何一次**成功**后重新拉开：重新登录后若再过期，还需要再跳一次。
   */
  let logoutFired = false;

  async function raw<T>(path: string, init: RequestInit = {}): Promise<Envelope<T>> {
    const headers = new Headers(init.headers ?? {});
    if (init.body !== undefined) headers.set('content-type', 'application/json');
    const token = tokenOf();
    if (token) headers.set('authorization', `Bearer ${token}`);
    let res: Response;
    try {
      res = await doFetch(`${baseUrl}${path}`, { ...init, headers });
    } catch (e) {
      // ★ reachable 只回答“主机通不通”：true ⇒ 失败在 CORS 白名单；false ⇒ 域名/网络/未部署
      const reachable = options.fetchImpl ? null : await probeReachable(baseUrl);
      throw new ApiFailure('NETWORK_ERROR', 0, {
        path,
        cause: String(e instanceof Error ? e.message : e),
        reachable,
        apiBase: baseUrl,
        origin: typeof window !== 'undefined' ? window.location.origin : '',
      });
    }
    const env = (await res.json().catch(() => null)) as Envelope<T> | null;
    if (!res.ok || !env || env.ok !== true) {
      const code = env?.error?.code ?? `HTTP_${res.status}`;
      const failure = new ApiFailure(code, res.status, (env?.error?.params ?? {}) as Record<string, unknown>);
      // ★ 会话失效只通知一次（其余并发 401 照样报错给页面，只是不重复跳转）
      if (failure.unauthorized && !logoutFired) {
        logoutFired = true;
        options.onUnauthorized?.();
      }
      throw failure;
    }
    logoutFired = false;
    return env;
  }

  const callList = async <T>(path: string, init: RequestInit = {}): Promise<Paged<T>> => {
    const env = await raw<T[]>(path, init);
    return { items: (env.data ?? []) as T[], meta: env.meta };
  };
  /** 单对象返回（写操作的结果）：★ 不能复用 callList —— 把对象当数组会让 `.map` 直接炸 */
  const callOne = async <T>(path: string, init: RequestInit = {}): Promise<T> => {
    const env = await raw<T>(path, init);
    return env.data as T;
  };

  const qs = (params: Record<string, string | number | undefined>) => {
    const search = new URLSearchParams();
    for (const [k, v] of Object.entries(params)) {
      if (v !== undefined && v !== '') search.set(k, String(v));
    }
    const s = search.toString();
    return s ? `?${s}` : '';
  };

  return {
    baseUrl,

    /** C1 运营登录（`admins` 表 + 独立密钥族；TOTP 在 totp_enabled=1 时必填） */
    login: (email: string, password: string, totp?: string) =>
      callOne<AdminSession>('/api/admin/auth/login', {
        method: 'POST',
        body: JSON.stringify(totp ? { email, password, totp } : { email, password }),
      }),

    /* ---------------- C3 模板管理 */
    templates: (p: { status?: string; locale?: string } = {}) =>
      callList<AdminTemplateRow>(`/api/admin/templates${qs(p)}`),
    versions: (slug: string) => callList<AdminVersionRow>(`/api/admin/templates/${slug}/versions`),
    /** ★ 上下架/元数据；public_version 改不了（只能走 promote） */
    patchTemplate: (slug: string, body: Record<string, unknown>) =>
      callOne<{ updated: boolean }>(`/api/admin/templates/${slug}`, { method: 'PATCH', body: JSON.stringify(body) }),
    promote: (slug: string, body: { version: string; previewExternal?: boolean; changelogPublic?: boolean }) =>
      callOne<PromoteResult>(`/api/admin/templates/${slug}/promote`, { method: 'POST', body: JSON.stringify(body) }),

    /* ---------------- C5 订单管理 */
    orders: (p: { status?: string; email?: string; template?: string; cursor?: string; limit?: number } = {}) =>
      callList<AdminOrderRow>(`/api/admin/orders${qs(p)}`),
    setOrderStatus: (id: string, body: { status: string; note?: string; providerOrderNo?: string }) =>
      callOne<OrderStatusResult>(`/api/admin/orders/${id}/status`, { method: 'POST', body: JSON.stringify(body) }),
    refund: (id: string, body: { providerEventId: string; reason?: string; amountCents?: number }) =>
      callOne<{ refunded: boolean; amountCents: number }>(`/api/admin/orders/${id}/refund`, {
        method: 'POST',
        body: JSON.stringify(body),
      }),

    /* ---------------- C7 授权管理 */
    licenses: (p: { email?: string; status?: string } = {}) => callList<AdminLicenseRow>(`/api/admin/licenses${qs(p)}`),
    issueLicense: (body: IssueLicenseInput) =>
      callOne<{ licenseId: string; plainKey: string }>('/api/admin/licenses', {
        method: 'POST',
        body: JSON.stringify(body),
      }),
    extendLicense: (id: string, body: { days: number; reason: string }) =>
      callOne<{ expiresAt: string }>(`/api/admin/licenses/${id}/extend`, {
        method: 'POST',
        body: JSON.stringify(body),
      }),
    revokeLicense: (id: string, reason: string) =>
      callOne<{ revoked: boolean }>(`/api/admin/licenses/${id}/revoke`, {
        method: 'POST',
        body: JSON.stringify({ reason }),
      }),

    /* ---------------- C12 GitHub 集成 */
    githubIntegration: () => callOne<GithubIntegrationView>('/api/admin/integrations/github'),
    patchGithubMapping: (body: { slug: string; githubRepo: string; githubPath: string }) =>
      callOne<{ updated: boolean }>('/api/admin/integrations/github/mapping', {
        method: 'PATCH',
        body: JSON.stringify(body),
      }),
    rotatePublishToken: () =>
      callOne<RotateTokenResult>('/api/admin/integrations/github/publish-token/rotate', { method: 'POST' }),

    /* ---------------- C8 部署任务 */
    deployments: (p: { status?: string; limit?: number } = {}) =>
      callList<AdminDeploymentRow>(`/api/admin/deployments${qs(p)}`),
    forceRetry: (id: string, reason: string) =>
      callOne<{ deploymentId: string; status: string; taskToken: string }>(`/api/admin/deployments/${id}/force-retry`, {
        method: 'POST',
        body: JSON.stringify({ reason }),
      }),
    precheck: (bindingId: string) => callOne<DeployPrecheck>(`/api/admin/deploy-precheck/${bindingId}`),

    /* ---------------- M2B-01 后端版本线（C14） */
    backends: (p: { status?: string } = {}) => callList<AdminBackendVersionRow>(`/api/admin/backends${qs(p)}`),
    publishBackend: (version: string) =>
      callOne<{ published: boolean; version: string }>(`/api/admin/backends/${version}/publish`, { method: 'POST' }),
    deprecateBackend: (version: string) =>
      callOne<{ deprecated: boolean; version: string }>(`/api/admin/backends/${version}/deprecate`, { method: 'POST' }),
    markBackendBatchUpgrade: (version: string, value: boolean) =>
      callOne<{ version: string; needsBatchUpgrade: boolean }>(`/api/admin/backends/${version}/mark-batch-upgrade`, {
        method: 'POST',
        body: JSON.stringify({ value }),
      }),
    setRecommendedBackend: (version: string) =>
      callOne<{ version: string; recommended: boolean }>(`/api/admin/backends/${version}/set-recommended`, {
        method: 'POST',
      }),

    /* ---------------- M2B-02 批量升级编排 */
    upgradeBatches: (p: { status?: string; limit?: number } = {}) =>
      callList<AdminUpgradeBatchRow>(`/api/admin/upgrade-batches${qs(p)}`),
    upgradeBatch: (id: string) => callOne<AdminUpgradeBatchRow>(`/api/admin/upgrade-batches/${id}`),
    upgradeBatchItems: (id: string, p: { status?: string; limit?: number } = {}) =>
      callList<UpgradeBatchItemRow>(`/api/admin/upgrade-batches/${id}/items${qs(p)}`),
    createUpgradeBatch: (body: {
      targetBackendVersion?: string;
      targetTemplateVersion?: string;
      filter?: { templateSlugs?: string[]; bindingIds?: string[] };
      maxConcurrency?: number;
      failThresholdPct?: number;
      note: string;
    }) => callOne<CreateBatchResult>('/api/admin/upgrade-batches', { method: 'POST', body: JSON.stringify(body) }),
    startUpgradeBatch: (id: string) =>
      callOne<{ batchId: string; status: string; rolloutPercent: number }>(`/api/admin/upgrade-batches/${id}/start`, {
        method: 'POST',
      }),
    advanceUpgradeBatch: (id: string, force = false) =>
      callOne<AdvanceBatchResult>(`/api/admin/upgrade-batches/${id}/advance`, {
        method: 'POST',
        body: JSON.stringify({ force }),
      }),
    pauseUpgradeBatch: (id: string, reason: string) =>
      callOne<{ batchId: string; status: string }>(`/api/admin/upgrade-batches/${id}/pause`, {
        method: 'POST',
        body: JSON.stringify({ reason }),
      }),
    resumeUpgradeBatch: (id: string) =>
      callOne<{ batchId: string; status: string }>(`/api/admin/upgrade-batches/${id}/resume`, { method: 'POST' }),
    abortUpgradeBatch: (id: string, reason: string) =>
      callOne<{ batchId: string; status: string }>(`/api/admin/upgrade-batches/${id}/abort`, {
        method: 'POST',
        body: JSON.stringify({ reason }),
      }),

    /* ---------------- M3-01 C2 运营仪表盘（一次 batch 出全部卡片，editor 可读） */
    dashboard: () => callOne<DashboardResp>('/api/admin/dashboard'),

    /* ---------------- M3-02 C9 链接健康检查 */
    linkHealth: (p: { status?: string; page?: number; size?: number } = {}) =>
      callOne<PagedRows<LinkHealthRow>>(`/api/admin/link-health${qs(p)}`),
    /** 单点即时重探（返回重探后的行） */
    recheckLinkHealth: (type: 'preview' | 'buyer_site', id: string) =>
      callOne<LinkHealthRow>(`/api/admin/link-health/${type}/${id}/recheck`, { method: 'POST' }),
    /** 手动推一次 probeHealth（等价提前跑一轮 cron，批上限内轮转） */
    scanLinkHealth: () => callOne<{ probed: number }>('/api/admin/link-health/scan', { method: 'POST' }),

    /* ---------------- B11/C7 人工协助工单队列 */
    /** 工单列表：服务端按「severity 高→低 + 创建时间倒序」排，cursor 为上一页最后一条 id */
    supportRequests: (p: { status?: string; severity?: string; limit?: number; cursor?: string } = {}) =>
      callList<SupportRequestRow>(`/api/admin/support/requests${qs(p)}`),
    /** 接单 / 改优先级 / 回复（status 进入 resolved|closed 时服务端自动写 resolved_at） */
    updateSupportRequest: (
      id: string,
      body: { status?: SupportRequestRow['status']; severity?: SupportRequestRow['severity']; reply?: string | null },
    ) =>
      callOne<{ updated: boolean }>(`/api/admin/support/requests/${id}`, {
        method: 'PATCH',
        body: JSON.stringify(body),
      }),

    /* ---------------- M3-03 C11 审计日志（仅 admin 可读，editor 服务端 403） */
    auditLogs: (
      p: {
        actor_id?: string;
        action?: string;
        target_type?: string;
        target_id?: string;
        from?: string;
        to?: string;
        page?: number;
        size?: number;
      } = {},
    ) => callOne<PagedRows<AuditLogRow>>(`/api/admin/audit-logs${qs(p)}`),

    /* ---------------- C1 运营自助：资料 / 密码（账号安全页） */
    adminProfile: () => callOne<AdminProfile>('/api/admin/auth/profile'),
    updateAdminProfile: (body: { username?: string; email?: string }) =>
      callOne<{ username: string; email: string }>('/api/admin/auth/profile', {
        method: 'PATCH',
        body: JSON.stringify(body),
      }),
    changeAdminPassword: (currentPassword: string, newPassword: string) =>
      callOne<{ changed: boolean }>('/api/admin/auth/password', {
        method: 'POST',
        body: JSON.stringify({ currentPassword, newPassword }),
      }),

    /* ---------------- M3-04 C1 TOTP 二因素（账号安全） */
    totpStatus: () => callOne<TotpStatus>('/api/admin/auth/totp/status'),
    totpEnroll: () => callOne<TotpEnrollResult>('/api/admin/auth/totp/enroll', { method: 'POST', body: '{}' }),
    totpActivate: (code: string) =>
      callOne<TotpEnabledResult>('/api/admin/auth/totp/activate', { method: 'POST', body: JSON.stringify({ code }) }),
    totpDeactivate: (password: string) =>
      callOne<TotpEnabledResult>('/api/admin/auth/totp/deactivate', {
        method: 'POST',
        body: JSON.stringify({ password }),
      }),

    /* ---------------- M2-09 C4 分类 / 标签管理（editor 可写；元数据无资金语义） */
    categories: (p: { locale?: string } = {}) => callList<AdminCategoryRow>(`/api/admin/categories${qs(p)}`),
    createCategory: (body: {
      slug: string;
      parentId?: string | null;
      sortOrder?: number;
      names: { locale: string; name: string; description?: string }[];
    }) =>
      callOne<{ id: string; slug: string; path: string; depth: number }>('/api/admin/categories', {
        method: 'POST',
        body: JSON.stringify(body),
      }),
    patchCategory: (
      id: string,
      body: {
        slug?: string;
        parentId?: string | null;
        sortOrder?: number;
        names?: { locale: string; name: string; description?: string }[];
      },
    ) =>
      callOne<{ id: string; updated: boolean }>(`/api/admin/categories/${id}`, {
        method: 'PATCH',
        body: JSON.stringify(body),
      }),
    deleteCategory: (id: string) =>
      callOne<{ id: string; deleted: boolean }>(`/api/admin/categories/${id}`, { method: 'DELETE' }),

    tags: (p: { locale?: string } = {}) => callList<AdminTagRow>(`/api/admin/tags${qs(p)}`),
    createTag: (body: { slug: string; type: string; names: { locale: string; name: string }[] }) =>
      callOne<{ id: string; slug: string; type: string }>('/api/admin/tags', {
        method: 'POST',
        body: JSON.stringify(body),
      }),
    patchTag: (id: string, body: { slug?: string; type?: string; names?: { locale: string; name: string }[] }) =>
      callOne<{ id: string; updated: boolean }>(`/api/admin/tags/${id}`, {
        method: 'PATCH',
        body: JSON.stringify(body),
      }),
    deleteTag: (id: string) => callOne<{ id: string; deleted: boolean }>(`/api/admin/tags/${id}`, { method: 'DELETE' }),

    /* ---------------- M2-11 C6 用户管理（ban/unban/reset 仅 admin；列表 editor 可读） */
    users: (p: { email?: string; status?: string; page?: number; size?: number } = {}) =>
      callOne<PagedRows<AdminUserRow>>(`/api/admin/users${qs(p)}`),
    banUser: (id: string, reason: string) =>
      callOne<{ id: string; status: string }>(`/api/admin/users/${id}/ban`, {
        method: 'POST',
        body: JSON.stringify({ reason }),
      }),
    unbanUser: (id: string, reason: string) =>
      callOne<{ id: string; status: string }>(`/api/admin/users/${id}/unban`, {
        method: 'POST',
        body: JSON.stringify({ reason }),
      }),
    resetUserPassword: (id: string) =>
      callOne<{ requested: boolean; email: string }>(`/api/admin/users/${id}/reset-password`, {
        method: 'POST',
        body: '{}',
      }),

    /* ---------------- M2-11 C10 系统配置（admin only） */
    configs: () => callList<AdminConfigRow>('/api/admin/configs'),
    putConfig: (key: string, body: { value: unknown; scope?: 'system' | 'env'; isSecret?: boolean }) =>
      callOne<{ key: string; scope: string; isSecret: boolean }>(`/api/admin/configs/${encodeURIComponent(key)}`, {
        method: 'PUT',
        body: JSON.stringify(body),
      }),
    deleteConfig: (key: string) =>
      callOne<{ key: string; deleted: boolean }>(`/api/admin/configs/${encodeURIComponent(key)}`, { method: 'DELETE' }),

    /* ---------------- M1-213 C13 内容翻译管理（读 admin+editor；写 admin only） */
    i18n: (p: { locale?: string; prefix?: string } = {}) => callOne<I18nListResult>(`/api/admin/i18n${qs(p)}`),
    i18nPending: () => callOne<{ items: I18nPendingItem[]; total: number }>('/api/admin/i18n/pending'),
    upsertI18n: (body: { msgKey: string; locale: string; value: string; dir?: 'ltr' | 'rtl' }) =>
      callOne<{ msgKey: string; locale: string }>('/api/admin/i18n', { method: 'PUT', body: JSON.stringify(body) }),
    deleteI18n: (msgKey: string, locale: string) =>
      callOne<{ deleted: boolean }>(`/api/admin/i18n${qs({ msgKey, locale })}`, { method: 'DELETE' }),
  };
}

export type AdminClient = ReturnType<typeof createAdminClient>;

/**
 * ★ 订单状态机（前端只用来"不给出非法按钮"，服务端 commerce.ts 才是权威）。
 * 与后端保持同一张表；后端改动会返回 CONFLICT + allowed 清单，页面据此提示而不是靠这里兜底。
 */
export const ORDER_TRANSITIONS: Record<string, string[]> = {
  pending: ['paid', 'canceled', 'failed'],
  paid: ['refunded'],
  canceled: [],
  refunded: [],
  failed: [],
};

export function allowedOrderStatuses(from: string): string[] {
  return ORDER_TRANSITIONS[from] ?? [];
}

/** 分 ⇒ 后台金额（★ 后台固定 zh-CN 数字格式：运营看的是对账，不要跟随浏览器语言换小数分隔符） */
export function formatCents(amountCents: number, currency: string): string {
  const fraction = amountCents % 100 === 0 ? 0 : 2;
  return new Intl.NumberFormat('zh-CN', {
    style: 'currency',
    currency,
    minimumFractionDigits: fraction,
    maximumFractionDigits: fraction,
  }).format(amountCents / 100);
}
